import cn from 'clsx'
import * as React from 'react'
import Button from 'components/ui/button'
import Heading from 'components/ui/heading'
import styles from './index.module.css'

type Props = {
  title?: React.ReactNode
  billing?: boolean
  planColour?: string
  disabled?: boolean
  description?: React.ReactNode
  children?: React.ReactNode
  footerLinks?:
    | boolean
    | Array<{
        caption?: string | React.ReactNode
        linkUrl?: string
        linkNode?: React.ReactNode
      }>
}

/**
 * @deprecated Use `Card` from `components/ds` instead.
 * @see {@link components/ds/Card}
 * @migration
 * - Replace `<Card>` with `<Card>` from components/ds
 * - DS Card is a simpler container; compose with other DS components for title/description:
 *   - title → Use `<Heading>` inside Card
 *   - description → Use `<Text>` inside Card
 *   - footerLinks → Compose with `<Flex>` and `<Link>` or `<Button>` components
 * - billing/planColour props have no DS equivalent; create a custom wrapper if needed
 * - disabled → Use CSS or wrap in a disabled state container
 * - DS Card supports `size` prop for padding variants
 *
 * A card component with optional title, description, and footer links
 *
 * @param title - Title to display at the top of the card
 * @param billing - Whether this is a billing card
 * @param planColour - Color for the plan border
 * @param disabled - Whether the card is disabled
 * @param description - Description content below the title
 * @param children - Main content of the card
 * @param footerLinks - Footer links array or boolean to show/hide
 * @returns The card component
 */
const Card = ({
  title,
  billing,
  planColour,
  disabled,
  footerLinks,
  description,
  children,
}: Props): React.ReactElement<'div'> => {
  return (
    <div
      className={cn(styles.cardContainer, {
        [styles.billingCard]: billing,
        [styles.billingCardDisabled]: disabled,
      })}
      style={
        planColour && {
          borderColor: `${planColour}`,
        }
      }
    >
      {title && (
        <div className={styles.cardTitle}>
          <Heading size="700">{title}</Heading>
        </div>
      )}
      {description && (
        <div className={styles.cardDescription}>{description}</div>
      )}
      {children}
      {Array.isArray(footerLinks) && (
        <div className={styles.cardFooter}>
          {footerLinks.map(({ linkNode, linkUrl, caption }, index) => {
            const marker =
              index < footerLinks.length - 1 ? (
                <b className={styles.cardFooterMarker}>·</b>
              ) : (
                ''
              )

            if (linkNode) {
              return (
                <React.Fragment key={index}>
                  <span className={styles.cardFooterLink}>{linkNode}</span>
                  {marker}
                </React.Fragment>
              )
            }

            if (caption && linkUrl) {
              return (
                <span key={index} className={styles.cardFooterLink}>
                  <Button
                    caption={caption}
                    appearance="inline-link"
                    link={linkUrl}
                    compact
                  />
                  {marker}
                </span>
              )
            }

            return null
          })}
        </div>
      )}
    </div>
  )
}

export default Card
